// src/pages/ApproveFlow.js
import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { apiURL } from "../config/config";
import { ClipLoader } from "react-spinners";
import BackButton from "../components/BackButton";
import { FiSearch } from "react-icons/fi";

import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const getUserDisplayName = (user = {}) => {
  const firstName = String(user?.firstName || "").trim();
  const lastName = String(user?.lastName || "").trim();
  const nickname = String(user?.nickname || "").trim();
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

  if (!fullName) return user?.username || "-";
  if (!nickname) return fullName;
  return `${fullName} (${nickname})`;
};

const ApproveFlow = () => {
  const [flowName, setFlowName] = useState("");
  const [levels, setLevels] = useState([{ level: 2, email: "" }]);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(true);
  const [userSearch, setUserSearch] = useState("");

  // ⭐ Users API
  const [users, setUsers] = useState([]);

  const filteredUsers = useMemo(() => {
    const keyword = userSearch.trim().toLowerCase();
    if (!keyword) return users;

    return users.filter((user) => {
      const haystack = [
        user?.firstName,
        user?.lastName,
        user?.nickname,
        user?.username,
        user?.email,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(keyword);
    });
  }, [userSearch, users]);

  // ⭐ Load Users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${apiURL}users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers(res.data);
      } catch (err) {
        console.error(err);
        toast.error("โหลดผู้ใช้ไม่สำเร็จ");
      } finally {
        setUsersLoading(false); // ⭐ สำคัญที่สุด!
      }
    };

    fetchUsers();
  }, []);

  // ⭐ Drag & Drop handler
  const onDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination) return;

    // ลากจาก users → level
    if (
      source.droppableId === "users" &&
      destination.droppableId.startsWith("level-")
    ) {
      const user = filteredUsers[source.index];
      if (!user) return;
      const levelIndex = Number(destination.droppableId.split("level-")[1]);

      const updated = [...levels];
      updated[levelIndex].email = user.username;

      setLevels(updated);
      return;
    }
  };

  // ⭐ Add Level
  const addLevel = () => {
    setLevels([...levels, { level: levels.length + 2, email: "" }]);
  };

  // ⭐ Remove Level
  const removeLevel = (index) => {
    if (levels.length === 1) return;
    const updated = levels.filter((_, i) => i !== index);
    setLevels(updated);
  };

  // ⭐ Input change
  const handleLevelChange = (index, value) => {
    const updated = [...levels];
    updated[index].email = value;
    setLevels(updated);
  };

  // ⭐ Submit Flow
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const invalid = levels.some((l) => !/\S+@\S+\.\S+/.test(l.email));

    if (!flowName || invalid) {
      toast.error("กรุณากรอก Flow Name และอีเมลให้ถูกต้อง");
      setLoading(false);
      return;
    }

    const payload = {
      name: flowName,
      approvalHierarchy: levels.map((l) => ({
        level: l.level,
        approver: l.email,
      })),
    };

    try {
      await axios.post(`${apiURL}approve-flows/create`, payload);
      toast.success("สร้าง Approve Flow สำเร็จ!");
      setFlowName("");
      setLevels([{ level: 2, email: "" }]);
    } catch (err) {
      console.error(err);
      toast.error("เกิดข้อผิดพลาดระหว่างบันทึก");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 transition-colors duration-300">
      <h1 className="text-xl font-semibold mb-6 text-center text-gray-800 dark:text-gray-200">
        Create Approve Flow
      </h1>

      {/* GRID LAYOUT */}
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* LEFT — FORM */}
          <div className="md:col-span-2">
            <div className="w-full bg-white dark:bg-gray-800 shadow-md rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Flow Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                    Flow Name
                  </label>
                  <input
                    type="text"
                    value={flowName}
                    onChange={(e) => setFlowName(e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                {/* LEVELS */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                    Approval Levels
                  </label>

                  {levels.map((level, index) => (
                    <Droppable
                      key={index}
                      droppableId={`level-${index}`}
                      direction="vertical"
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg mb-3 border border-gray-300 dark:border-gray-600"
                        >
                          <span className="font-semibold text-gray-700 dark:text-gray-200">
                            Level {level.level}
                          </span>

                          <input
                            type="email"
                            value={level.email}
                            onChange={(e) =>
                              handleLevelChange(index, e.target.value)
                            }
                            placeholder="Approver email (ลาก user ใส่ได้)"
                            className="mt-2 w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-800 dark:text-white"
                          />

                          <button
                            type="button"
                            onClick={() => removeLevel(index)}
                            disabled={levels.length === 1}
                            className="mt-2 px-3 py-1 bg-red-500 text-white rounded disabled:bg-gray-500"
                          >
                            Remove
                          </button>

                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={addLevel}
                  className="w-full bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg"
                >
                  Add Level
                </button>

                <div className="flex justify-end gap-3">
                  <BackButton label="ย้อนกลับ" />
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-2 rounded-lg flex items-center justify-center"
                  >
                    {loading ? (
                      <ClipLoader size={18} color="#fff" />
                    ) : (
                      "Create Flow"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* RIGHT — USERS LIST */}
          <div>
            <h2 className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-2">
              Users (ลากไปใส่ Level ได้)
            </h2>

            <div className="relative mb-3">
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="ค้นหาชื่อ, nickname, email"
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-gray-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:border-indigo-400"
              />
            </div>

            <Droppable droppableId="users" direction="vertical">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="space-y-3 bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-300 dark:border-gray-700 max-h-[75vh] overflow-y-auto"
                >
                  {/* ⭐ Loading State */}
                  {usersLoading ? (
                    <div className="flex flex-col gap-3">
                      {[...Array(5)].map((_, i) => (
                        <div
                          key={i}
                          className="w-full h-14 rounded-lg bg-gray-200 dark:bg-gray-700 animate-pulse"
                        ></div>
                      ))}
                    </div>
                  ) : (
                    filteredUsers.map((user, index) => (
                      <Draggable
                        key={user._id}
                        draggableId={user._id}
                        index={index}
                      >
                        {(drag) => (
                          <div
                            ref={drag.innerRef}
                            {...drag.draggableProps}
                            {...drag.dragHandleProps}
                            className="p-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg shadow cursor-pointer"
                          >
                            <div className="font-semibold">
                              {getUserDisplayName(user)}
                            </div>
                            <div className="text-sm opacity-90">
                              {user.username}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))
                  )}

                  {!usersLoading && filteredUsers.length === 0 && (
                    <div className="rounded-lg border border-dashed border-gray-300 px-3 py-6 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
                      ไม่พบผู้ใช้ที่ตรงกับคำค้นหา
                    </div>
                  )}

                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </div>
      </DragDropContext>

      <ToastContainer />
    </div>
  );
};

export default ApproveFlow;
